import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/Kpi";
import { eurShort, num } from "@/lib/format";

const PERIODI = [["S1", "I semestre"], ["S2", "II semestre"], ["ANNO", "Annuale"]];
const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const inputCls = "rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0018A8]/30";
const pct = (v) => (v === null || v === undefined ? "–" : `${v.toLocaleString("it-IT", { maximumFractionDigits: 2 })}%`);

/** Rappel (opzionali, semestrali/annuali) e file provvigioni del dealer, con anteprima del report completo. */
export function RappelProvvigioni({ codice, anno, rappelIniziale, puoModificare }) {
  const [rappel, setRappel] = useState(rappelIniziale || []);
  const [report, setReport] = useState(null);
  const [salvo, setSalvo] = useState(false);
  const fileRef = useRef(null);
  const carica = () => api.get(`/dealers/${codice}/report-completo?anno=${anno}`).then((r) => setReport(r.data)).catch(() => setReport(null));
  useEffect(() => { carica(); }, [codice, anno]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRappel(rappelIniziale || []); }, [rappelIniziale]);

  const aggiorna = (i, k, v) => setRappel((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const salva = async () => {
    setSalvo(true);
    try {
      const r = await api.put(`/dealers/${codice}/rappel`, { rappel });
      setRappel(r.data.rappel);
      toast.success("Rappel salvati");
      carica();
    } catch (e) { toast.error(apiError(e)); } finally { setSalvo(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/dealers/${codice}/provvigioni?anno=${anno}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Provvigioni caricate: ${r.data.righe} mesi`);
      carica();
    } catch (e) { toast.error(apiError(e)); }
    if (fileRef.current) fileRef.current.value = "";
  };
  const provv = report?.provvigioni?.curr;

  return (
    <Card className="p-5 space-y-5" data-testid="dealer-rappel-provvigioni">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">Report dealer completo · rappel e provvigioni</div>
          <p className="text-xs text-slate-500 mt-1">Rappel opzionali (semestrali e/o annuale) con proiezione e progressivo; provvigioni da file Excel (colonne: Mese, Provvigioni tasso, Provvigioni assicurative). Redditività = provvigioni / erogato.</p>
        </div>
        <a href={`/api/dealers/${codice}/report-completo.pdf?anno=${anno}&t=${Date.now()}`} data-testid="btn-report-completo-pdf"
          className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480] transition-colors duration-200">
          Report completo PDF
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3" data-testid="dealer-rappel">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Rappel {anno}</div>
          {rappel.filter((r) => Number(r.anno) === Number(anno)).length === 0 && <p className="text-xs text-slate-400">Nessun rappel impostato</p>}
          {rappel.map((r, i) => Number(r.anno) === Number(anno) && (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`rappel-riga-${i}`}>
              <select className={inputCls} value={r.periodo} disabled={!puoModificare} data-testid={`rappel-periodo-${i}`}
                onChange={(e) => aggiorna(i, "periodo", e.target.value)}>
                {PERIODI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <input type="number" min="0" step="10000" className={`${inputCls} w-40 text-right`} value={r.importo ?? ""} disabled={!puoModificare}
                data-testid={`rappel-importo-${i}`} placeholder="Importo obiettivo €" onChange={(e) => aggiorna(i, "importo", e.target.value)} />
              {puoModificare && (
                <button type="button" className="text-xs text-red-600 hover:underline" data-testid={`rappel-rimuovi-${i}`}
                  onClick={() => setRappel((x) => x.filter((_, j) => j !== i))}>rimuovi</button>
              )}
            </div>
          ))}
          {puoModificare && (
            <div className="flex gap-2">
              <button type="button" data-testid="rappel-aggiungi" className="px-3 py-1.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50"
                onClick={() => setRappel((x) => [...x, { anno, periodo: "S2", importo: "" }])}>+ Aggiungi rappel</button>
              <button type="button" data-testid="rappel-salva" disabled={salvo} onClick={salva}
                className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs hover:bg-slate-700 disabled:opacity-50">Salva rappel</button>
            </div>
          )}
          {report?.rappel?.map((r) => (
            <div key={r.periodo} className="rounded-md bg-slate-50 p-3 text-sm space-y-1" data-testid={`rappel-stato-${r.periodo}`}>
              <div className="font-semibold text-slate-800">Rappel {r.label} · obiettivo {eurShort(r.importo)}</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
                <span>Raggiunto <b className="font-mono">{eurShort(r.raggiunto)}</b> ({pct(r.perc_raggiunto)})</span>
                <span>{anno - 1}: <b className="font-mono">{eurShort(r.raggiunto_prec)}</b></span>
                <span>Proiezione <b className="font-mono">{eurShort(r.proiezione)}</b></span>
                <span className={(r.scostamento_perc ?? 0) < 0 ? "text-red-600" : "text-emerald-700"}>scostamento {r.scostamento_perc === null ? "–" : `${r.scostamento_perc > 0 ? "+" : ""}${pct(r.scostamento_perc)}`}</span>
                {r.provvigioni > 0 && <span>Provvigioni <b className="font-mono">{eurShort(r.provvigioni)}</b> · redditività {pct(r.redditivita)}</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3" data-testid="dealer-provvigioni">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Provvigioni {anno}</div>
            {puoModificare && (
              <label className="px-3 py-1.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50 cursor-pointer" data-testid="provvigioni-upload-label">
                Carica file Excel
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="provvigioni-upload-input" onChange={(e) => upload(e.target.files?.[0])} />
              </label>
            )}
          </div>
          {provv?.righe?.length ? (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-400">
                <tr><th className="text-left py-1">Mese</th><th className="text-right">Tasso</th><th className="text-right">Assicurative</th><th className="text-right">Totale</th><th className="text-right">Redditività</th></tr>
              </thead>
              <tbody>
                {provv.righe.map((r) => (
                  <tr key={r.mese} className="border-t border-slate-100" data-testid="provvigioni-riga">
                    <td className="py-1 text-slate-600">{MESI[parseInt(r.mese.slice(0, 2), 10) - 1]}</td>
                    <td className="text-right font-mono">{num(r.provvigioni_tasso, 2)}</td>
                    <td className="text-right font-mono">{num(r.provvigioni_assicurative, 2)}</td>
                    <td className="text-right font-mono font-semibold">{num(r.provvigioni, 2)}</td>
                    <td className="text-right font-mono text-[#0018A8]">{pct(r.redditivita)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-300 font-semibold" data-testid="provvigioni-totale">
                  <td className="py-1">Totale</td>
                  <td className="text-right font-mono">{num(provv.totale.provvigioni_tasso, 2)}</td>
                  <td className="text-right font-mono">{num(provv.totale.provvigioni_assicurative, 2)}</td>
                  <td className="text-right font-mono">{num(provv.totale.provvigioni, 2)}</td>
                  <td className="text-right font-mono text-[#0018A8]">{pct(provv.totale.redditivita)}</td>
                </tr>
              </tbody>
            </table>
          ) : <p className="text-xs text-slate-400">Nessun file provvigioni caricato per il {anno}</p>}
        </div>
      </div>
    </Card>
  );
}
